import React, { FC } from 'react';
import withStyles from 'src/common/withStyles';

import { decorateComponent, getClassNameFor } from '../../helpers';

import Avatar from '../Avatar';
import NavDropdown from '../NavDropdown';
import SubNavigation from './SubNavigation';
import Svg from '../Svg';

import s from './Navigation.module.scss';
import UnauthenticatedNavItems from './UnauthenticatedNavItems';
import { ImmutableUser } from '../../../types/user/User';
import configItems, { NavigationConfigType } from './config';
import NavItem from './NavItem';
import useMediaQuery, { sharedMediaQueries } from '../../hooks/useMediaQuery';
import NavModal from '../NavModal';

type Props = {
  user: ImmutableUser;
  classNameModifier?: string;
  /**
   * Determines the type of navigation items presented within header
   * Defaults to `undefined`
   */
  navigationVariant?: 'owner' | 'owner-calendar-inbox';
};
const Navigation: FC<Props> = ({ classNameModifier = '', user, navigationVariant }) => {
  const isDesktop = useMediaQuery(sharedMediaQueries.m);
  const isLoggedIn = user.get('loggedIn');
  const isOwner = user.get('owner_approved');
  const modifiers = [classNameModifier, 'mainNav', isLoggedIn ? 'authenticated' : 'anonymous'];
  const modifiersString = modifiers.filter(Boolean).join(' ');

  const menuItems = isOwner
    ? configItems.filter(
      (config) => config.isHeaderNavItem?.owner && (config.roles == null || config.roles.includes('owner')),
    )
    : configItems.filter(
      (config) => config.isHeaderNavItem?.renter && (config.roles == null || config.roles.includes('renter')),
    );

  return (
    <nav className={getClassNameFor(s, 'root', classNameModifier)}>
      <ul className={getClassNameFor(s, 'menu', classNameModifier)}>
        {isLoggedIn && (
          <>
            {menuItems.map((data: NavigationConfigType) => {
              const screenSizeModifiers = navigationVariant === 'owner-calendar-inbox'
                ? data.isHeaderNavItem?.ownerCalendarInbox?.join(' ')
                : data.isHeaderNavItem?.[isOwner ? 'owner' : 'renter']?.join(' ');

              const leftAligned = data.key === 'charge' ? 'leftAligned' : '';

              if (!screenSizeModifiers) return null;
              return (
                <NavItem
                  key={data.key}
                  classNameModifier={`${modifiersString} primary ${data.classNameModifier} ${screenSizeModifiers} ${leftAligned}`}
                  to={data.to}
                  external={!data.internal}
                  icon={data.icon}
                  openInNewTab={data.openInNewTab}
                >
                  {data.label}
                </NavItem>
              );
            })}
          </>
        )}
        {!isLoggedIn && <UnauthenticatedNavItems classNameModifier={modifiersString} />}

        <li
          className={getClassNameFor(
            s,
            'item',
            [...modifiers, 'dropdown'].filter(Boolean).join(' '),
          )}
        >
          {!isDesktop && (
            <NavModal
              classNameModifier="menuItems"
              heading={(
                <Svg
                  href="#burger-menu"
                  className={s.hamburger}
                />
              )}
            >
              <SubNavigation />
            </NavModal>
          )}
          {isDesktop && (
            <NavDropdown
              className={getClassNameFor(s, 'link', modifiersString)}
              data-test="MainMenuDropDown"
              aria-label="Main menu"
              heading={(
                <>
                  <Svg
                    href="#burger-menu"
                    className={s.hamburger}
                  />
                  {isLoggedIn && (
                    <div className={s.user}>
                      <Avatar
                        user={user.toJS()}
                        classNameModifier="nav noInteraction noMargin"
                        size="default"
                      />
                      <span className={s.userName}>{user.get('first_name')}</span>
                    </div>
                  )}
                </>
              )}
            >
              <SubNavigation />
            </NavDropdown>
          )}
        </li>
      </ul>
    </nav>
  );
};

const decorators = [withStyles(s)];

export default decorateComponent(Navigation, decorators);
